import { Form, Formik, FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IContactData } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { requestCreateLead } from '@modules/ContactManager/components/Tabs/components/Leads/requests';
import { ILead, SOURCES } from '@modules/ContactManager/components/Tabs/components/Leads/types/interfaces/interfaces';
import { getMonthCount, removeExtraSpaces, withQueryParams } from '@shared/commonFunctions';
import { ASSET_TYPE_OPTIONS } from '@shared/constants/lists';

import { useLeadSource } from '@shared/hooks/useLeadSource';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { useAzureMaps } from '@shared/hooks/useAzureMaps';
import { OneToOneConsentPresentedAndAccepted } from '@shared/constants/constants';
import { CreateContactForm } from './components/CreateContactForm';
import { IContactAddress, ICreateContact, ILeadSource, requestCreateContact } from './queries';
import { contactManagerFormSchema } from './validation';

const initialState = {
  secondarySource: '',
  firstName: '',
  middleInitial: '',
  lastName: '',
  suffix: '',
  nickName: '',
  maidenName: '',
  ssn: '',
  dateOfBirth: '',
  homePhone: '',
  workPhone: '',
  workPhoneExtension: '',
  cellPhone: '',
  email: '',
  militaryStatus: '',
  maritalStatus: '',
  driverLicenseNumber: '',
  driverLicenseExpireDate: '',
  driverLicenseIssuesOnDate: '',
  driverLicenseState: '',
  currentAddress: {},
  previousAddress: {},
  currentEmployer: {},
  previousEmployer: {},
  asset: {
    assetType: ASSET_TYPE_OPTIONS.find((option) => option.value === 'Car')?.value,
    vin: '',
    year: null,
    make: '',
    model: '',
    kbbTrim: '',
    actualNadaTradeIn: null,
    mileage: '',
    selectedTradeInValue: null,
    assetCondition: '',
  },
};

const CreateContactPage = () => {
  const navigate = useNavigate();
  const { updateCurrentTab, openTab } = useTabs();
  const { getCounty } = useAzureMaps();
  const { leadSourceData: leadSources, leadSourcesError } = useLeadSource<ILeadSource[]>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    updateCurrentTab({
      route: `${location.pathname.toString()}${location.search}`,
      title: 'New Contact',
    });
  }, []);

  const primarySourceId = useMemo(
    () => (leadSources?.length ? leadSources.find((source: ILeadSource) => source.name === SOURCES.PHONE_APPEND)?.id : ''),
    [leadSources]
  );

  const afterCreateContactAction = useCallback((data: IContactData) => {
    const route = withQueryParams('/contact-manager', { id: data.id });
    const title = `${data.firstName} ${data.lastName}`;

    openTab({ title, route });
    navigate(route);
  }, []);

  const getAddressCounty = useCallback(
    async (data: IContactAddress) => {
      const fullAddress = `${data.streetLine1} ${data.city} ${data.state} ${data.zip}`;
      const currentCounty = (await getCounty(fullAddress)) ?? '';
      return currentCounty;
    },
    [getCounty]
  );

  const handleSubmit = useCallback(
    async (values: ICreateContact, formikActions: FormikHelpers<ICreateContact>) => {
      setIsLoading(true);
      const { asset, currentAddress, previousAddress, currentEmployer, previousEmployer, ...restValues } = values;
      const currAddressCounty = await getAddressCounty(currentAddress);
      const prevAddressCounty = await getAddressCounty(previousAddress);
      const requestData = {
        ...restValues,
        asset: asset.assetType && asset.model && asset.make && asset.year ? asset : null,
        currentAddress: {
          ...currentAddress,
          monthsAtAddress: getMonthCount(currentAddress),
          county: currAddressCounty,
          streetLine1: removeExtraSpaces(currentAddress?.streetLine1 ?? ''),
          city: removeExtraSpaces(currentAddress?.city ?? ''),
          unit: removeExtraSpaces(currentAddress?.unit ?? ''),
        },
        previousAddress: {
          ...previousAddress,
          monthsAtAddress: getMonthCount(previousAddress),
          county: prevAddressCounty,
          streetLine1: removeExtraSpaces(previousAddress?.streetLine1 ?? ''),
          city: removeExtraSpaces(previousAddress?.city ?? ''),
          unit: removeExtraSpaces(previousAddress?.unit ?? ''),
        },
        currentEmployer: { ...currentEmployer, monthsAtJob: getMonthCount(currentEmployer) },
        previousEmployer: { ...previousEmployer, monthsAtJob: getMonthCount(previousEmployer) },
      };
      requestCreateContact(requestData as ICreateContact).then(async (response) => {
        if (response?.data) {
          const data = response.data;
          const payload = {
            contactId: data.id,
            assetId: data.assetResponse?.id || null,
            primarySourceId: primarySourceId,
            secondarySourceId: values?.secondarySource,
            loanApplicationId: null,
            marketingInfo: null,
            score: null,
            channelType: 1,
            createWithNumericId: true,
            oneToOneConsentPresentedAndAccepted: OneToOneConsentPresentedAndAccepted.Yes,
          } as ILead;
          await requestCreateLead(payload);

          afterCreateContactAction(data);
        }
        setIsLoading(false);
      });
      formikActions.setSubmitting(false);
    },
    [primarySourceId]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialState as unknown as ICreateContact}
      validationSchema={contactManagerFormSchema}
      onSubmit={handleSubmit}
    >
      {(props) => (
        <Form onSubmit={props.handleSubmit}>
          <CreateContactForm
            isLoading={isLoading}
            leadSources={leadSources || []}
            leadSourcesError={leadSourcesError}
          />
        </Form>
      )}
    </Formik>
  );
};

export const CreateContact = withOpenTab(CreateContactPage, 'Create Contact');

